import { formatMoney, useLocale, useTranslation } from '@multaqa/i18n'
import { KuficSkeleton } from '@multaqa/ui/components/kufic/kufic-skeleton'
import { Alert, AlertDescription, AlertTitle } from '@multaqa/ui/components/ui/alert'
import { Badge } from '@multaqa/ui/components/ui/badge'
import { Button } from '@multaqa/ui/components/ui/button'
import { ConfirmDialog } from '@multaqa/ui/components/ui/confirm-dialog'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import { PageHeader } from '@multaqa/ui/components/ui/page-header'
import { PageShell } from '@multaqa/ui/components/ui/page-shell'
import { Panel, PanelBody } from '@multaqa/ui/components/ui/panel'
import { cn } from '@multaqa/ui/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { PlusIcon, Settings2Icon, Trash2Icon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { meQuery } from '@/lib/me'
import {
  useDeleteWorkflow,
  useWorkflowDefinitions,
  useWorkflowDocumentTypes,
  type WorkflowDefinition,
} from './api'
import { WorkflowDialog } from './workflow-dialog'

function WorkflowField({
  label,
  children,
  className,
  valueClassName,
  labelClassName,
}: {
  label: string
  children: ReactNode
  className?: string
  valueClassName?: string
  labelClassName?: string
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <dt className={cn('text-small text-muted-foreground', labelClassName)}>{label}</dt>
      <dd className={cn('mt-1 min-w-0 break-words text-body', valueClassName)}>{children}</dd>
    </div>
  )
}

export function WorkflowSettingsScreen() {
  const { t } = useTranslation('workflow')
  const { locale } = useLocale()
  const { data: me } = useQuery(meQuery)
  const companyId = me?.active_company_id ?? null
  const definitions = useWorkflowDefinitions(companyId)
  const documentTypes = useWorkflowDocumentTypes(companyId)
  const [editing, setEditing] = useState<WorkflowDefinition | null | 'new'>(null)
  const [removing, setRemoving] = useState<WorkflowDefinition | null>(null)
  const deleteMutation = useDeleteWorkflow(companyId ?? '')

  if (!companyId) return null

  const pageHeader = (showCreateAction: boolean) => (
    <PageHeader
      trail={[{ label: t('nav.configure'), href: '/settings' }]}
      title={t('settings.title')}
      description={`${t('settings.subtitle')} ${t('settings.description')}`}
      actions={
        showCreateAction ? (
          <Button onClick={() => setEditing('new')}>
            <PlusIcon className="size-3.5" aria-hidden="true" />
            {t('settings.create')}
          </Button>
        ) : undefined
      }
    />
  )

  if (definitions.isLoading || documentTypes.isLoading) {
    return (
      <PageShell className="h-full min-h-0" data-testid="workflow-settings-screen">
        {pageHeader(false)}
        <div
          className="mx-auto w-full max-w-5xl space-y-4 py-4"
          role="status"
          aria-label={t('state.loading')}
          aria-busy="true"
        >
          {[0, 1, 2].map((row) => (
            <div
              key={row}
              className="grid grid-cols-2 gap-4 border-b border-border-subtle py-4 sm:grid-cols-5"
            >
              <KuficSkeleton width="72%" row={row * 2} />
              <KuficSkeleton width="48%" row={row * 2 + 1} />
            </div>
          ))}
        </div>
      </PageShell>
    )
  }

  if (definitions.isError || documentTypes.isError) {
    return (
      <PageShell className="h-full min-h-0" data-testid="workflow-settings-screen">
        {pageHeader(false)}
        <Alert
          severity="danger"
          className="mx-auto w-full max-w-5xl"
          data-testid="workflow-settings-error"
        >
          <AlertTitle>
            {definitions.isError ? t('state.error') : t('state.optionsError')}
          </AlertTitle>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <Button
              variant="outline"
              loading={definitions.isFetching || documentTypes.isFetching}
              onClick={() => {
                void definitions.refetch()
                void documentTypes.refetch()
              }}
            >
              {t('action.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      </PageShell>
    )
  }

  const list = definitions.data ?? []
  const documentLabel = (definition: WorkflowDefinition) =>
    documentTypes.data?.data.find((type) => type.key === definition.document_type)?.label[locale] ??
    t('settings.unknownDocument')

  return (
    <PageShell className="h-full min-h-0" data-testid="workflow-settings-screen">
      {pageHeader(true)}
      <main className="min-h-0 flex-1 overflow-auto py-4">
        {list.length === 0 ? (
          <EmptyState
            icon={<Settings2Icon className="size-5" aria-hidden="true" />}
            title={t('settings.empty')}
            description={t('settings.emptyDescription')}
            action={
              <Button onClick={() => setEditing('new')}>
                <PlusIcon className="size-3.5" aria-hidden="true" />
                {t('settings.create')}
              </Button>
            }
          />
        ) : (
          <Panel className="mx-auto w-full max-w-5xl">
            <div className="hidden grid-cols-[minmax(0,2fr)_minmax(0,0.7fr)_minmax(0,1.6fr)_minmax(0,0.8fr)_auto] gap-x-4 border-b border-border px-bleed py-3 text-small text-muted-foreground lg:grid">
              <span>{t('settings.columns.document')}</span>
              <span>{t('settings.columns.steps')}</span>
              <span>{t('settings.columns.threshold')}</span>
              <span>{t('settings.columns.status')}</span>
              <span className="text-end">{t('settings.columns.actions')}</span>
            </div>
            <PanelBody className="py-0">
              <ol className="divide-y divide-border-subtle">
                {list.map((definition) => (
                  <li
                    key={definition.id}
                    className="py-4 first:pt-0 last:pb-0"
                    data-testid={`workflow-row-${definition.id}`}
                  >
                    <dl className="grid min-w-0 grid-cols-2 gap-x-4 gap-y-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,0.7fr)_minmax(0,1.6fr)_minmax(0,0.8fr)_auto] lg:items-center">
                      <WorkflowField
                        className="col-span-2 lg:col-span-1"
                        label={t('settings.columns.document')}
                        labelClassName="lg:sr-only"
                      >
                        {documentLabel(definition)}
                      </WorkflowField>
                      <WorkflowField
                        label={t('settings.columns.steps')}
                        labelClassName="lg:sr-only"
                        valueClassName="tabular-nums"
                      >
                        {definition.steps.length} {t('settings.stepCount')}
                      </WorkflowField>
                      <WorkflowField
                        label={t('settings.columns.threshold')}
                        labelClassName="lg:sr-only"
                        valueClassName="tabular-nums lg:text-end"
                      >
                        {definition.threshold_amount
                          ? definition.threshold_currency
                            ? formatMoney(
                                definition.threshold_amount,
                                definition.threshold_currency,
                                locale,
                              )
                            : definition.threshold_amount
                          : t('settings.anyAmount')}
                      </WorkflowField>
                      <WorkflowField
                        label={t('settings.columns.status')}
                        labelClassName="lg:sr-only"
                      >
                        <Badge tone={definition.enabled ? 'success' : 'neutral'}>
                          {definition.enabled ? t('settings.enabled') : t('settings.disabled')}
                        </Badge>
                      </WorkflowField>
                      <WorkflowField
                        label={t('settings.columns.actions')}
                        labelClassName="lg:sr-only"
                        valueClassName="flex flex-wrap items-center gap-1 sm:justify-end"
                      >
                        <Button variant="ghost" size="sm" onClick={() => setEditing(definition)}>
                          {t('action.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setRemoving(definition)}
                          aria-label={t('action.delete')}
                        >
                          <Trash2Icon className="size-3.5 text-danger" aria-hidden="true" />
                        </Button>
                      </WorkflowField>
                    </dl>
                  </li>
                ))}
              </ol>
            </PanelBody>
          </Panel>
        )}
      </main>
      {editing ? (
        <WorkflowDialog
          companyId={companyId}
          editing={editing === 'new' ? null : editing}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null)
          }}
        />
      ) : null}
      {removing ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setRemoving(null)
          }}
          title={t('settings.deleteTitle')}
          description={t('settings.deleteDescription')}
          confirmLabel={t('action.delete')}
          cancelLabel={t('action.cancel')}
          closeLabel={t('action.cancel')}
          tone="destructive"
          pending={deleteMutation.isPending}
          onConfirm={() =>
            deleteMutation.mutate(removing.id, { onSuccess: () => setRemoving(null) })
          }
          testId="workflow-delete-dialog"
        />
      ) : null}
    </PageShell>
  )
}
